import { PERSIST_STORE_VERSION, type PersistedBucketVersions } from './persistedBuckets';

export const DEFAULT_PERSIST_STORAGE_NAME = '<grid-id>';
const LEGACY_BUCKET_VERSION_FALLBACK = 0;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;
const isValidVersion = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value) && value >= 0;
const readBucketVersion = (value: unknown, key: keyof PersistedBucketVersions, fallback: number): number => {
    if (!isRecord(value)) return fallback;
    const maybeVersion = value[key];
    return isValidVersion(maybeVersion) ? maybeVersion : fallback;
};

const toPersistedPayload = (value: unknown): unknown => {
    if (typeof value === 'string') {
        return JSON.parse(value);
    }
    return value;
};

export const loadPersistedStateIntoLocalStorage = (value: unknown, storageName = DEFAULT_PERSIST_STORAGE_NAME): void => {
    const parsed = toPersistedPayload(value);
    const candidateState = isRecord(parsed) && isRecord(parsed.state) ? parsed.state : parsed;
    const payloadVersion = isRecord(parsed) ? parsed.version : undefined;
    const persistedVersion = isValidVersion(payloadVersion) ? payloadVersion : PERSIST_STORE_VERSION;
    const bucketVersionsSource = isRecord(parsed) ? parsed.bucketVersions : undefined;
    // Legacy snapshots may not include bucketVersions; when absent/missing, force migration from 0.
    const bucketVersions: PersistedBucketVersions = {
        global: readBucketVersion(bucketVersionsSource, 'global', LEGACY_BUCKET_VERSION_FALLBACK),
        layers: readBucketVersion(bucketVersionsSource, 'layers', LEGACY_BUCKET_VERSION_FALLBACK),
        designSchemes: readBucketVersion(bucketVersionsSource, 'designSchemes', LEGACY_BUCKET_VERSION_FALLBACK),
    };

    if (!isRecord(candidateState)) {
        throw new Error('Invalid state payload');
    }

    localStorage.removeItem(storageName);

    localStorage.setItem(
        storageName,
        JSON.stringify({
            version: persistedVersion,
            bucketVersions,
            state: candidateState,
        })
    );
};
